import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetFinancialPositionResponse } from '@/api/handlers/resources/analytics/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { AnalyticsQueryKeys } from './analyticsQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * The whole Financial Position screen in one read.
 *
 * Called by all four overview cards. That is deliberate rather than wasteful:
 * TanStack dedupes on the query key, so the cards keep owning their own data —
 * each is still mountable anywhere on its own — while the screen makes a single
 * request. Splitting this into four endpoints would cost four round trips and
 * let the cards disagree about the same moment in time.
 *
 * `asOf` is left to the server when omitted, so "today" is decided once, by the
 * clock that owns the ledger.
 */
export const useFinancialPositionQuery = (asOf?: string) => {
    const { apiClient } = useApi();
    const { i18n } = useTranslation();
    const locale = i18n.language;

    return useQuery<ApiResponse<GetFinancialPositionResponse>>({
        queryKey: AnalyticsQueryKeys.financialPosition(asOf, locale),
        queryFn: () =>
            apiClient.analytics.getFinancialPosition({ asOf, locale }),
        staleTime: STALE_TIME_MS,
    });
};
